import { forwardRef, useMemo } from 'react';
import { FormInstance } from 'antd';
import {
  AccessGroupAutoComplete,
  DataObjectGroupRootOption,
  DirectoryFilter,
  DirectoryFilterProps,
  FieldProps,
  Select,
  SelectProps,
  useInfiniteSearchDataObjectGroupRootsQuery,
  useInfiniteSearchSelect,
  UserDirectoryFilter,
} from '@atlas-frontend/sdk';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './UserDirectory.translations.json';

const { addTranslations, t } = i18nNsFactory('user-directory');
addTranslations(translations);

interface UserDirectoryFilterProps
  extends DirectoryFilterProps<
    Pick<UserDirectoryFilter, 'accessGroupId' | 'groupId'>
  > {
  initialValues?: Pick<UserDirectoryFilter, 'accessGroupId' | 'groupId'>;
}

export const UserDirectoryFilterPanel = forwardRef<
  FormInstance,
  UserDirectoryFilterProps
>((props, ref) => {
  const { initialValues, ...rest } = props;

  const filterFields: FieldProps<Record<string, any>>[] = useMemo(
    () => [
      {
        name: 'accessGroupId',
        label: t('search-panel.accessGroup'),
        initialValue: initialValues?.accessGroupId,
        input: () => (
          <AccessGroupAutoComplete
            id="accessGroupId-filter"
            placeholder={t('search-panel.accessGroup')}
          />
        ),
      },
      {
        name: 'groupId',
        label: t('search-panel.group'),
        initialValue: initialValues?.groupId,
        input: () => (
          <DataObjectGroupRootAutoComplete
            id="groupIds-filter"
            placeholder={t('search-panel.group')}
          />
        ),
      },
    ],
    [initialValues?.accessGroupId, initialValues?.groupId],
  );

  return <DirectoryFilter ref={ref} fields={filterFields} {...rest} />;
});

function DataObjectGroupRootAutoComplete(props: SelectProps<number>) {
  const { onClear, onFocus, onSearch, value, ...rest } = props;
  const selectedGroupIds = typeof value === 'number' ? [value] : undefined;
  const { data, selectProps } = useInfiniteSearchSelect({
    useSearchHook: useInfiniteSearchDataObjectGroupRootsQuery,
    searchOptions: {
      includeSystemic: true,
      selectedGroupIds,
      sort: 'isSystemic,code',
    },
    onClear,
    onFocus,
    onSearch,
  });
  const groupOptions = useMemo(() => {
    const optionsById = new Map<number, DataObjectGroupRootOption>();
    data?.pages.forEach((page) =>
      page.content.forEach((option) => optionsById.set(option.value, option)),
    );
    const options = Array.from(optionsById.values());

    return [
      {
        key: 'groups',
        label: t('search-panel.groups'),
        options: options.filter((option) => !option.systemic),
      },
      {
        key: 'descriptions',
        label: t('search-panel.descriptions'),
        options: options.filter((option) => option.systemic),
      },
    ];
  }, [data?.pages]);

  return (
    <Select {...rest} {...selectProps} value={value} options={groupOptions} />
  );
}
